import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Nav,
  Table,
} from "react-bootstrap";
import "./../../assets/scss/requisition/index.scss";
import {
  ApproveBlueModalIcon,
  DeleteIcon,
  EditIcon,
  MessageIcon,
  RefreshIcon,
  ApproveCheckIcon,
  ViewIcon,
} from "../Icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { isEmpty, lowerCase, max } from "lodash";
import { format } from "date-fns";
import { requisitionActions } from "../../utils/reactQueryActions";
import Avatar from "../utils/Avatar";
import { useAuth } from "../../hooks/useAuth";
import { useBackendUrl, useEffectOnce } from "../../utils/hooks";

import { last } from "lodash";
import { LinkWithQuery as Link } from "../utils/LinkWithQuery";
import eventBus from "../../utils/EventBus";
import {
  fetchActionsUtil,
  formatDate,
  formatDateTime,
  getUserThatRequestedToMe,
  isInventoryOrFundRequest,
  resolveApprovalBadgeBg,
  styleHelper,
} from "../../utils/helpers";
import { useCanGoBack, useIsAdmin } from "../../utils/hooks";
import CachedIcon from "mdi-react/CachedIcon";
import ConfirmDialog from "../ConfirmDialogue";
import { toast } from "react-toastify";
import { useStoreState } from "easy-peasy";
import RequisitionApproveFooter from "../Requisition/RequisitionApproveFooter";
import { uniq } from "lodash";
import { size } from "lodash";
import { confirmable, createConfirmation } from "react-confirm";
import TableComponent from "../TableComponent";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import TransactionDetailsModal from "./TransactionDetailsModal";

function Comments({ reportComments, refetch, requisitionid }) {
  const { user: authUser, backendUrl } = useAuth();

  useEffectOnce(() => {
    refetch();
  });

  const deleteComments = async payload => {
    let response = await fetch(
      `${backendUrl}/api/customers/report/${payload.id}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };
  const deleteCommentsMutation = useMutation(
    payload => deleteComments(payload),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        if (refetch) refetch();
      },
      onError: ({ message = "" }) => {
        toast.error(`Unable to perform action: ${message}`);
      },
    }
  );

  const deleteComment = async comment => {
    if (
      await ConfirmDialog({
        title: "Delete Comment",
        description: `"${comment.comment}"`,
      })
    ) {
      deleteCommentsMutation.mutate({
        ...comment,
      });
    }
  };

  const editComment = async comment => {
    await createConfirmation(confirmable(EditLeaveComment))({
      oldComment: comment.comment,
      id: comment.id,
      refetch,
    });
  };

  return (
    <>
      {!isEmpty(reportComments) ? (
        <div className="comments">
          {reportComments.map((comment, index) => {
            let { date, time } = formatDateTime(comment.regdate);
            return (
              <p key={index} className=" d-flex align-items-center gap-1">
                <div className="timeline border rounded-circle">
                  <ApproveCheckIcon />
                </div>
                <b>{comment.senderDetails?.Name || "Unknown Staff"}:</b>{" "}
                <span className="flex-grow-1">{comment.comment}</span>
                <span className="text-muted fs-6 text-nowrap">
                  {time} <br /> {date}
                </span>
                {authUser.Staff_ID === comment.senderID && (
                  <span>
                    {" "}
                    <Button
                      variant=""
                      className="p-1"
                      onClick={() => editComment(comment)}
                    >
                      <EditIcon />
                    </Button>
                    <Button
                      variant=""
                      className="p-1"
                      onClick={() => deleteComment(comment)}
                    >
                      <DeleteIcon />
                    </Button>
                  </span>
                )}
              </p>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

function LeaveComment({
  proceed,
  show,
  customer,
  backendUrl,
  authUser,
  previousRequisitionTrackId,
  requisitionTitle,
  refetch,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [comment, setComment] = useState("");

  const submitReport = async () => {
    try {
      let response = await fetch(`${backendUrl}/api/customers/report`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ comment, customerID: customer.Cust_ID }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        response = response.json();
        throw new Error(response.message);
      }
      response = response.json();
      toast.success(response.message);
      return response;
    } catch (error) {
      toast.error(error.message);
    } finally {
      proceed(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => !isLoading && proceed(false)}
      dialogClassName="requisition-details-modal"
      centered={true}
      animation={false}
      enforceFocus={false}
      fullscreen={styleHelper.isMobile}
    >
      <Modal.Header closeButton />
      <Modal.Body className="approve-action">
        <div className="text-info-h">
          <ApproveBlueModalIcon className="mb-4" />
          <h2>Feedback</h2>
        </div>
        <Form.Group className="my-4">
          <Form.Label className="d-none">Feedback :</Form.Label>
          <label className="border p-0 rounded pb-2 message-area d-block">
            <Form.Control
              name="message"
              as="textarea"
              value={comment}
              onChange={e => setComment(e.target.value)}
              className="w-100 border-0 p-3"
              placeholder="Enter your comment"
              rows={4}
              autoFocus
            />
          </label>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span />
        <div className="proceed-actions">
          <Button
            disabled={isLoading}
            onClick={() => proceed(false)}
            variant="white"
            className="border bg-white"
          >
            Cancel
          </Button>
          <Button
            disabled={isLoading}
            onClick={() => submitReport()}
            variant="primary"
          >
            {isLoading ? "Please wait" : "Send"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function EditLeaveComment({ proceed, show, id, refetch, oldComment }) {
  const backendUrl = useBackendUrl();
  const [isLoading, setIsLoading] = useState(false);
  const [comment, setComment] = useState("");

  useEffectOnce(() => {
    setComment(oldComment);
  });

  const editComment = async () => {
    try {
      setIsLoading(true);
      let response = await fetch(`${backendUrl}/api/customers/report`, {
        method: "PATCH",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          id,
          comment,
        }),
      });

      if (!response.ok) {
        response = await response.json();
        throw new Error(response.message);
      }

      const { data } = await response.json();
      toast.success("Comment Edited");
      if (refetch) refetch();
      proceed(true);
    } catch (err) {
      console.log(err);
      toast.error("Unable to Edit Comment");
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Modal
      show={show}
      onHide={() => !isLoading && proceed(false)}
      dialogClassName="requisition-details-modal"
      centered={true}
      animation={false}
      enforceFocus={false}
      fullscreen={styleHelper.isMobile}
    >
      <Modal.Header closeButton />
      <Modal.Body className="approve-action">
        <div className="text-info-h">
          <ApproveBlueModalIcon className="mb-4" />
          <h2>Edit Comment</h2>
        </div>
        <Form.Group className="my-4">
          <Form.Label className="d-none">Comment :</Form.Label>
          <label className="border p-0 rounded pb-2 message-area d-block">
            <Form.Control
              name="message"
              as="textarea"
              value={comment}
              onChange={e => setComment(e.target.value)}
              className="w-100 border-0 p-3"
              placeholder="Enter your comment"
              rows={4}
              autoFocus
            />
          </label>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span />
        <div className="proceed-actions">
          <Button
            disabled={isLoading}
            onClick={() => proceed(false)}
            variant="white"
            className="border bg-white"
          >
            Cancel
          </Button>
          <Button
            disabled={isLoading}
            onClick={() => editComment()}
            variant="primary"
          >
            {isLoading ? "Please wait" : "Save"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default function CustomerFeedBackPage({ customer, onClose }) {
  const generalSettings = useStoreState(state => state.generalSettings);
  const { user: authUser, backendUrl } = useAuth();
  // const queryClient = useQueryClient();
  let { id } = useParams();
  const [activeKey, setActiveKey] = useState("feedback");
  const isAdmin = useIsAdmin();

  const handleSelect = eventKey => setActiveKey(eventKey);
  let navigate = useNavigate();
  const goBack = useCanGoBack();
  const [reciever, setReceiver] = useState();
  const [report, setReport] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState([]);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [searchParams] = useSearchParams();
  const customerId = searchParams.get("customerId");

  const { data: customerData, isLoading } = useQuery([customerId], () =>
    fetchActionsUtil(
      `${backendUrl}/api/customers/single?customerId=${customerId}`
    )
  );

  customer = customerData?.data || customer;

  const getReport = async id => {
    console.log(customer);
    // await waitFor(5000);
    let response = await fetch(
      `${backendUrl}/api/customers/report/${customer.Cust_ID}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    response = await response.json();
    if (response.success) {
      console.log(response.data);
      setReport(response.data);
      return response.data;
    }
  };

  const handleViewTransaction = transaction => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const getTransactions = async () => {
    console.log(customer);
    // await waitFor(5000);
    let response = await fetch(
      `${backendUrl}/api/permits/all?customerId=${customer.Cust_ID}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    response = await response.json();
    console.log("transaction Data: ", response.data);
    setTransactions(response.data);
    return response.data;
  };

  const { error, data, isSuccess, refetch, isFetching } = useQuery({
    queryKey: ["customerReport"],
    queryFn: () => getReport(id),
    staleTime: Infinity,
    keepPreviousData: true,
  });

  const { data: transactionData, refetch: refetchTransaction } = useQuery({
    queryKey: ["customerTransaction"],
    queryFn: getTransactions,
    staleTime: Infinity,
    keepPreviousData: true,
  });

  useEffect(() => {
    console.log(transactionData);
    refetchTransaction();
    return () => {
      console.log("unmountting component");
    };
  }, [transactionData]);

  const makeComment = async () => {
    await createConfirmation(confirmable(LeaveComment))({
      customer,
      backendUrl,
    });
    refetch();
    // await LeaveCommentDialog({
    //   comment,
    // });
  };

  const tableHead = () => {
    return (
      <thead className="position-sticky top-0">
        <tr>
          <th />

          <th>Transaction ID</th>
          <th>Item Name</th>
          <th>Feedbacks</th>
          <th>Unit price</th>
          <th>Sub total</th>
          <th>customer name</th>
          <th>Status</th>
          <th>created date</th>
          {/* <th>Issued Date</th> */}
        </tr>
      </thead>
    );
  };

  const tableData = (item, index) => {
    return (
      <>
        <td>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              className="bg-white border-0"
              bsPrefix="print more"
            >
              <DotsVerticalIcon />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{
                strategy: "fixed",
              }}
              renderOnMount
              className="dropdown-with-icons"
            >
              <Dropdown.Item
                as="button"
                onClick={() => {
                  handleViewTransaction(item);
                }}
              >
                <ViewIcon /> View Transaction
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </td>
        <td>{item?.TransactionID}</td>
        <td>{item?.Item_Name || "---"}</td>
        <td>{item?.feedback.length}</td>
        <td>{item?.Unit_Price || "Not Assigned"}</td>
        <td>{item?.SubTotal}</td>
        <td>{item?.customer?.firstname || "Customer"}</td>
        <td>{item?.Status}</td>
        <td>
          {item?.Date_Log ? (
            <>
              <div>{`${formatDate(
                new Date(item?.Date_Log),
                "dd MMM, yyyy"
              )}`}</div>
              <div
                className=""
                style={{ fontSize: "12px", fontStyle: "italic" }}
              >
                {`${formatDate(new Date(item?.Date_Log), "hh:mm a")}`}
              </div>
            </>
          ) : (
            "N/A"
          )}
        </td>
        {/* <td
          className={`
            ${item?.expiry ? "text-danger fw-bold" : "text-success fw-bold"}
          `}
          style={{ textTransform: "capitalize" }}
        >
          {item?.expiry ? item?.deployment_id ? "Used" : "Deactivated" : "Active"}
        </td>
        <td>
          {item?.created_at ? (
            <>
              <div>{`${formatDate(
                new Date(item?.created_at),
                "dd MMM, yyyy"
              )}`}</div>
              <div
                className=""
                style={{ fontSize: "12px", fontStyle: "italic" }}
              >
                {`${formatDate(new Date(item?.created_at), "hh:mm a")}`}
              </div>
            </>
          ) : (
            "N/A"
          )}
        </td> */}
      </>
    );
  };

  return (
    <main className="cash-book ">
      <div className="content border rounded-4">
        <div className="content-main">
          <div className="content-body">
            <header>
              <h4>
                {" "}
                Customer Feedback{" "}
                <Button onClick={() => refetch()} variant="">
                  <RefreshIcon />
                </Button>
              </h4>
              <p>More information about the customer</p>
              <div
                className="actions"
                style={{
                  zIndex: "10",
                }}
              />
            </header>
            {!isLoading && (
              <div className="px-md-4">
                <div className="d-flex mb-2 justify-content-between align-items-end">
                  <Nav
                    variant="tabs"
                    onSelect={handleSelect}
                    // defaultActiveKey="1"
                    activeKey={activeKey}
                  >
                    <Nav.Item>
                      <Nav.Link className={`ga-nav-tab`} eventKey="feedback">
                        General Feedback
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link className={`ga-nav-tab`} eventKey="quotation">
                        Loading order
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link className={`ga-nav-tab`} eventKey="invoice">
                        Invoice
                      </Nav.Link>
                    </Nav.Item>
                    {/*
            <Nav.Item>
              <Nav.Link className={`ga-nav-tab`} eventKey="loadingOrder">
                Loading Order
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link className={`ga-nav-tab`} eventKey="invoice">
                Invoice
              </Nav.Link>
            </Nav.Item> */}
                  </Nav>
                  <InputGroup
                    className="w-50 mb-8"
                    style={{ justifySelf: "end" }}
                  >
                    <Form.Control
                      placeholder="Search transaction..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setSearchTerm("")}
                      >
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                  {/*
          <Link
            to={`/requisition/request/conversations/${id}`}
            state={{ backgroundLocation: location }}
            className="btn border text-primary message-btn"
          >
            <MessageProcessingOutlineIcon />
          </Link> */}
                </div>

                {activeKey === "feedback" && (
                  <>
                    <div className="info p-3 my-3 border rounded">
                      <p className="border-bottom pb-3 mb-3 d-flex gap-3 title">
                        <span>From:</span>
                        <span className="fw-5">{authUser?.Name}</span>
                      </p>

                      <h5>{customer?.LastName}</h5>
                      <p>{customer?.Dept}</p>

                      <div className="d-md-flex">
                        <Table borderless className="info-table mb-0 mb-md-3">
                          <tbody>
                            <tr>
                              <td>Customer Name :</td>
                              <td>{customer.LastName}</td>
                            </tr>
                            <tr>
                              <td>Date:</td>
                              <td>
                                {report.at(-1)?.regdate
                                  ? format(
                                      new Date(report.at(-1)?.regdate),
                                      "dd MMM, yyyy"
                                    )
                                  : "..."}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                        {/* <Table borderless className="info-table mb-0 mb-md-3">
                  <tbody>
                    <tr>
                      <td>Status :</td>
                      <td>
                        <ApprovalBadge
                          text={requisition?.status}
                          className="approval"
                          bg={resolveApprovalBadgeBg(requisition?.status)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Requester:</td>
                      <td className="fw-5">
                        {requisition?.requestbyUser?.Name ||
                          requisition?.sentByName ||
                          "..."}
                      </td>
                    </tr>
                  </tbody>
                </Table> */}
                      </div>
                    </div>

                    <div className="approval p-3 my-3 border rounded">
                      <p className="border-bottom pb-3 mb-3 title">
                        <span>Feedback</span>
                      </p>

                      <div className="approval-history">
                        <Comments
                          refetch={refetch}
                          reportComments={report}
                          // requisitionid={requisition?.requestid}
                        />
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <Button
                        variant="light-blue"
                        onClick={() => makeComment()}
                      >
                        <MessageIcon />
                        {"  "}
                        Add feedback
                      </Button>
                    </div>
                  </>
                )}
                {activeKey === "quotation" && (
                  <>
                    <TableComponent
                      tableRowDoubleClick={(e, el) => {
                        handleViewTransaction(el);
                      }}
                      responsive
                      borderless
                      striped
                      tableHeadsFunction={tableHead}
                      mainDataArray={
                        transactions.permits?.filter(
                          permit =>
                            permit.Status !== "Invoiced" &&
                            permit.TransactionID.includes(searchTerm)
                        ) || []
                      }
                      className="product-table text-nowrap"
                      tableDataRowFunction={tableData}
                    />
                  </>
                )}
                {activeKey === "invoice" && (
                  <>
                    <TableComponent
                      tableRowDoubleClick={(e, el) => {
                        handleViewTransaction(el);
                      }}
                      responsive
                      borderless
                      striped
                      tableHeadsFunction={tableHead}
                      mainDataArray={
                        transactions.permits?.filter(
                          permit =>
                            permit.Status === "Invoiced" &&
                            permit.TransactionID.includes(searchTerm)
                        ) || []
                      }
                      className="product-table text-nowrap"
                      tableDataRowFunction={tableData}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showTransactionDetails && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          show={showTransactionDetails}
          refetch={refetchTransaction}
          onHide={() => setShowTransactionDetails(false)}
        />
      )}
    </main>
  );
}
